import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { addOrUpdateCapability } from "../../../services/capabilitiesApi";
import { dashboardToast } from "../../../utils/dashboardToast";
import { QUESTION_STATE } from "../../../utils/subCapabilityQuestionUtils";
import {
  buildCapabilitySavePayload,
  createEmptyCapabilityFormData,
  createEmptySubCapability,
  type CapabilityFormData,
  hasNamedSubCapabilities,
  mapApiCapabilityToFormData,
  withAddedMaturityOption,
  withCustomMaturityToggle,
  withMaturityOptionPatch,
  withQuestionList,
  withRemovedMaturityOption,
} from "./capabilityFormShared";

export function useCapabilityForm(options: {
  businessTypes: Array<{ id: number; name?: string }>;
  onSaved?: () => void;
}) {
  const { businessTypes, onSaved } = options;
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);
  const [formData, setFormData] = useState<CapabilityFormData>(() =>
    createEmptyCapabilityFormData([])
  );
  const [saving, setSaving] = useState(false);
  const [expandedSubCaps, setExpandedSubCaps] = useState(() => new Set<number>());
  const [expandedPanels, setExpandedPanels] = useState(() => new Set<string>());

  const resetExpanded = useCallback(() => {
    setExpandedSubCaps(new Set());
    setExpandedPanels(new Set());
  }, []);

  const loadFromApi = useCallback((capability: Record<string, unknown>) => {
    setFormData(mapApiCapabilityToFormData(capability));
    resetExpanded();
  }, [resetExpanded]);

  const resetForCreate = useCallback(() => {
    setFormData(createEmptyCapabilityFormData(businessTypes));
    resetExpanded();
  }, [businessTypes, resetExpanded]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value, type } = e.target;
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : name === "businessTypeId" || name === "frameworkId"
              ? value === ""
                ? ""
                : Number(value)
              : value,
      }));
    },
    []
  );

  const setFrameworkId = useCallback((next: string) => {
    setFormData((prev) => ({
      ...prev,
      frameworkId: next === "" ? "" : Number(next),
    }));
  }, []);

  const setBusinessTypeId = useCallback((next: string) => {
    setFormData((prev) => ({
      ...prev,
      businessTypeId: next === "" ? "" : Number(next),
    }));
  }, []);

  const handleSubCapabilityChange = useCallback(
    (index: number, field: string, value: unknown) => {
      setFormData((prev) => {
        const newSubCapabilities = [...prev.subCapabilities];
        if (!newSubCapabilities[index]) {
          newSubCapabilities[index] = createEmptySubCapability(prev.id);
        }
        newSubCapabilities[index] = {
          ...newSubCapabilities[index],
          [field]: value,
        };

        if (field === "useCustomMaturity") {
          newSubCapabilities[index] = withCustomMaturityToggle(
            newSubCapabilities[index],
            Boolean(value)
          );
        }

        return {
          ...prev,
          subCapabilities: newSubCapabilities,
        };
      });
    },
    []
  );

  const handleQuestionListChange = useCallback(
    (index: number, stateKind: typeof QUESTION_STATE.current, nextList: unknown[]) => {
      setFormData((prev) => {
        const newSubCapabilities = [...prev.subCapabilities];
        if (!newSubCapabilities[index]) {
          newSubCapabilities[index] = createEmptySubCapability(prev.id);
        }
        newSubCapabilities[index] = withQuestionList(
          newSubCapabilities[index],
          stateKind,
          nextList
        );
        return { ...prev, subCapabilities: newSubCapabilities };
      });
    },
    []
  );

  const handleCapabilityQuestionListChange = useCallback(
    (stateKind: typeof QUESTION_STATE.current, nextList: unknown[]) => {
      setFormData((prev) => withQuestionList(prev, stateKind, nextList));
    },
    []
  );

  const handleCapabilityAssessmentChange = useCallback((field: string, value: unknown) => {
    setFormData((prev) => {
      if (field === "useCustomMaturity") {
        return withCustomMaturityToggle(prev, Boolean(value));
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const handleCapabilityMaturityOptionChange = useCallback(
    (optionIndex: number, patch: Record<string, unknown>) => {
      setFormData((prev) => withMaturityOptionPatch(prev, optionIndex, patch));
    },
    []
  );

  const handleAddCapabilityMaturityOption = useCallback(() => {
    setFormData((prev) => withAddedMaturityOption(prev));
  }, []);

  const handleRemoveCapabilityMaturityOption = useCallback((optionIndex: number) => {
    setFormData((prev) => withRemovedMaturityOption(prev, optionIndex));
  }, []);

  const handleMaturityOptionChange = useCallback(
    (subIndex: number, optionIndex: number, patch: Record<string, unknown>) => {
      setFormData((prev) => {
        const newSubCapabilities = [...prev.subCapabilities];
        newSubCapabilities[subIndex] = withMaturityOptionPatch(
          newSubCapabilities[subIndex],
          optionIndex,
          patch
        );
        return { ...prev, subCapabilities: newSubCapabilities };
      });
    },
    []
  );

  const handleAddMaturityOption = useCallback((subIndex: number) => {
    setFormData((prev) => {
      const newSubCapabilities = [...prev.subCapabilities];
      newSubCapabilities[subIndex] = withAddedMaturityOption(newSubCapabilities[subIndex]);
      return { ...prev, subCapabilities: newSubCapabilities };
    });
  }, []);

  const handleRemoveMaturityOption = useCallback((subIndex: number, optionIndex: number) => {
    setFormData((prev) => {
      const newSubCapabilities = [...prev.subCapabilities];
      newSubCapabilities[subIndex] = withRemovedMaturityOption(
        newSubCapabilities[subIndex],
        optionIndex
      );
      return { ...prev, subCapabilities: newSubCapabilities };
    });
  }, []);

  const toggleSubCapExpanded = useCallback((index: number) => {
    setExpandedSubCaps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const togglePanelExpanded = useCallback((panelKey: string) => {
    setExpandedPanels((prev) => {
      const next = new Set(prev);
      if (next.has(panelKey)) {
        next.delete(panelKey);
      } else {
        next.add(panelKey);
      }
      return next;
    });
  }, []);

  const addSubCapability = useCallback(() => {
    let newIndex = 0;
    setFormData((prev) => {
      newIndex = prev.subCapabilities.length;
      return {
        ...prev,
        subCapabilities: [...prev.subCapabilities, createEmptySubCapability(prev.id)],
      };
    });
    setExpandedSubCaps((expanded) => new Set([...expanded, newIndex]));
    setExpandedPanels(
      (expanded) =>
        new Set([...expanded, `${newIndex}-questions`, `${newIndex}-maturity`])
    );
  }, []);

  const removeSubCapability = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      subCapabilities: prev.subCapabilities.filter((_, i) => i !== index),
    }));
    setExpandedSubCaps((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    setExpandedPanels((prev) => {
      const next = new Set<string>();
      prev.forEach((key) => {
        const [rawIndex, panel] = String(key).split("-");
        const i = Number(rawIndex);
        if (Number.isNaN(i) || !panel) return;
        if (i < index) next.add(`${i}-${panel}`);
        else if ( i > index) next.add(`${i - 1}-${panel}`);
      });
      return next;
    });
  }, []);

  const chooseSingleCapabilityAssessment = useCallback(() => {
    if (hasNamedSubCapabilities(formData.subCapabilities)) {
      dashboardToast.warning(
        "Remove or clear sub-capabilities before assessing as a single capability."
      );
      return;
    }
    setFormData((prev) => ({ ...prev, subCapabilities: [] }));
    resetExpanded();
  }, [formData.subCapabilities, resetExpanded]);

  const chooseSubCapabilityAssessment = useCallback(() => {
    setFormData((prev) => {
      if (prev.subCapabilities.length > 0) return prev;
      return {
        ...prev,
        subCapabilities: [createEmptySubCapability(prev.id)],
      };
    });
    setExpandedSubCaps((prev) => (prev.size > 0 ? prev : new Set([0])));
    setExpandedPanels((prev) =>
      prev.size > 0 ? prev : new Set(["0-questions", "0-maturity"])
    );
  }, []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!formData.capabilityName || !formData.capabilityDescription || !formData.businessTypeId) {
        if (!formData.businessTypeId) {
          dashboardToast.error("Please select a user type.");
        }
        return;
      }

      try {
        setSaving(true);
        await addOrUpdateCapability(buildCapabilitySavePayload(formData));
        dashboardToast.success(
          formData.id > 0 ? "Capability updated successfully" : "Capability created successfully"
        );
        onSavedRef.current?.();
      } catch (err) {
        console.error("Error saving capability:", err);
        const message =
          err instanceof Error ? err.message : "Failed to save capability. Please try again.";
        dashboardToast.error(message);
      } finally {
        setSaving(false);
      }
    },
    [formData]
  );

  return {
    formData,
    setFormData,
    resetExpanded,
    saving,
    expandedSubCaps,
    expandedPanels,
    loadFromApi,
    resetForCreate,
    handleChange,
    setFrameworkId,
    setBusinessTypeId,
    handleSubCapabilityChange,
    handleQuestionListChange,
    handleCapabilityQuestionListChange,
    handleCapabilityAssessmentChange,
    handleCapabilityMaturityOptionChange,
    handleAddCapabilityMaturityOption,
    handleRemoveCapabilityMaturityOption,
    handleMaturityOptionChange,
    handleAddMaturityOption,
    handleRemoveMaturityOption,
    toggleSubCapExpanded,
    togglePanelExpanded,
    addSubCapability,
    removeSubCapability,
    chooseSingleCapabilityAssessment,
    chooseSubCapabilityAssessment,
    handleSubmit,
    hasNamedSubCapabilities,
  };
}
